import { useParams } from 'react-router-dom'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import React, { useState } from 'react'
import { TableColumn } from 'react-data-table-component'
import FormattedDate from '../../../components/FormattedDate'
import { ActionButton } from '../../../components/DataTable/ActionButton'
import { Col, Container, Dropdown, Row } from 'react-bootstrap'
import { CustomDropdownItem } from '../../../components/Dropdown/CustomDropdownItem'
import { ZocksDataTable } from '../../../components/DataTable/ZocksDataTable'
import { InviteResponse, useGetInvitesQuery } from '../../../store/auth/auth'
import { DeleteInviteModal } from '../DeleteInviteModal'
import { DEFAULT_PAGE_SIZE } from '../../../common/constants'
import { formatDate } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { Roles } from '../../../components/Roles/Roles'
import { ResendInviteModal } from '../ResendInviteModal'
import { PendingInvitationFilterModel, PendingInvitationsFilter } from './PendingInvitationsFilter'
import { Divider } from '../../../components/Divider/Divider'
import { useDebouncedCallback } from 'use-debounce'
import { defaultPendingInvitationsFilter } from './utils'
import { dateSortValue } from '../utils'

export const PendingInvitationsTab = () => {
  const { userId = null } = useParams()

  const accountId = useAppSelector(selectAccountId)
  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE)
  const [page, setPage] = useState(1)
  const [selectedInviteId, setSelectedInviteId] = useState<string | null>(userId)
  const [showResendInviteModal, setShowResendInviteModal] = useState<boolean>(false)
  const [showDeleteInviteModal, setShowDeleteInviteModal] = useState<boolean>(false)
  const [filter, setFilter] = useState<PendingInvitationFilterModel>(defaultPendingInvitationsFilter)
  const { data, isFetching } = useGetInvitesQuery(
    {
      accountId: accountId,
      pageRequest: { page: page - 1, size: perPage },
      filter,
    },
    { refetchOnMountOrArgChange: true },
  )

  const handlePageChange = (page: number) => {
    setPage(page)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPage(page)
    setPerPage(newPerPage)
  }

  const handleOnClickResendInvite = (invite: InviteResponse) => {
    setSelectedInviteId(invite.id)
    setShowResendInviteModal(true)
  }

  const handleResendInviteModalHide = () => {
    setShowResendInviteModal(false)
    setSelectedInviteId(null)
  }

  const handleOnClickDeleteInvite = (invite: InviteResponse) => {
    setSelectedInviteId(invite.id)
    setShowDeleteInviteModal(true)
  }

  const handleDeleteInviteModalHide = () => {
    setShowDeleteInviteModal(false)
    setSelectedInviteId(null)
  }

  const columns: TableColumn<InviteResponse>[] = [
    {
      name: 'Name',
      selector: (row) => {
        return row.user.name ? row.user.name : row.user.firstName || row.user.lastName ? `${row.user.firstName ?? ''} ${row.user.lastName ?? ''}` : ''
      },
      sortable: true,
    },
    {
      name: 'Email',
      selector: (row) => row.user.email,
      sortable: true,
      width: '22%',
    },
    {
      name: 'Role',
      selector: (row) => JSON.stringify(row.user.roleIds),
      sortable: true,
      wrap: true,
      cell: (row) => <Roles userRoleIds={row.user.roleIds} />,
    },
    {
      name: 'Created at',
      selector: (row) => dateSortValue(row.createdAt),
      sortable: true,
      cell: (row) => <FormattedDate date={row.createdAt ? formatDate(row.createdAt) : ''} />,
    },
    {
      name: 'Expires at',
      selector: (row) => dateSortValue(row.expiresAt),
      sortable: true,
      cell: (row) => <FormattedDate date={row.expiresAt ? formatDate(row.expiresAt) : ''} invalidDateText={''} />,
    },
    {
      name: 'Status',
      sortable: true,
      cell: (row) => {
        return <div>{row.state === 'failedToDeliver' ? 'failed to deliver' : row.state}</div>
      },
      conditionalCellStyles: [
        {
          when: (row) => row.state === 'expired' || row.state === 'failedToDeliver',
          style: {
            color: 'var(--zocks-danger)',
          },
        },
        {
          when: (row) => row.state === 'pending',
          style: {
            color: 'var(--zocks-warning-text)',
          },
        },
      ],
    },
    {
      ignoreRowClick: true,
      right: true,
      button: true,
      allowOverflow: true,
      width: '3rem',
      cell: (row) => (
        <ActionButton>
          <Dropdown.Item as={CustomDropdownItem} onClick={() => handleOnClickResendInvite(row)}>
            Resend
          </Dropdown.Item>
          <Dropdown.Item as={CustomDropdownItem} onClick={() => handleOnClickDeleteInvite(row)}>
            Delete
          </Dropdown.Item>
        </ActionButton>
      ),
    },
  ]

  const handleFilterChanged = useDebouncedCallback((filter: PendingInvitationFilterModel) => {
    setFilter(filter)
  }, 500)

  return (
    <Container>
      {selectedInviteId ? (
        <>
          <DeleteInviteModal
            invite={data?.data.find((invite) => invite.id === selectedInviteId)}
            show={showDeleteInviteModal}
            onHide={handleDeleteInviteModalHide}
          />
          <ResendInviteModal
            invite={data?.data.find((invite) => invite.id === selectedInviteId)}
            show={showResendInviteModal}
            onHide={handleResendInviteModalHide}
          />
        </>
      ) : null}
      <Row>
        <Col className='p-0'>
          <PendingInvitationsFilter onFilterChanged={handleFilterChanged} />
          <Divider className='mt-4' />
          <ZocksDataTable
            customStyles={{
              cells: {
                style: {
                  fontSize: '0.75rem',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '1.125rem' /* 150% */,
                },
              },
            }}
            columns={columns}
            data={data?.data ?? []}
            progressPending={isFetching}
            pagination
            paginationServer
            paginationTotalRows={data?.page.totalElements}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
          />
        </Col>
      </Row>
    </Container>
  )
}
